/**
 * CauseChain — "How we got here" on a thread's brief (kanban 303c204a; Scott
 * picked layout C, "cause chain", on review e328a768 over five others).
 *
 * A row of small cards joined by arrows, one cause per card, oldest first.
 * The step the thread is at today is marked "now"; steps that have not
 * happened yet are dashed. Clicking a step opens, under the row, the fact it
 * rests on, what Merlin checked for that fact, and the Do next items it
 * drives (numbered as the Do next list numbers them). Merlin files the
 * chain; the resolution against facts and steps is in merlinBrief.ts.
 */
import { useState } from 'react';

import { AssistantMarkdown } from '../../components/agentConsole/AssistantTurnView';
import type { ChainStep } from './merlinBrief';
import { defaultChainStep } from './merlinBrief';
import styles from './CauseChain.module.css';

const STATE_WORD: Record<ChainStep['state'], string> = {
  past: '',
  now: 'now',
  next: 'not yet',
};

export function CauseChain({
  steps,
  numbered = true,
}: {
  steps: ChainStep[];
  /** False where Do next is grouped by desk and carries no numbers to match. */
  numbered?: boolean;
}) {
  const [selected, setSelected] = useState<string | null>(() => defaultChainStep(steps));
  const current = steps.find((s) => s.key === selected) ?? null;

  return (
    <div className={styles.wrap} aria-label="How we got here">
      <span className={styles.label}>How we got here</span>
      <ol className={styles.chain}>
        {steps.map((s, i) => (
          <li key={s.key} className={styles.item}>
            <button
              type="button"
              className={styles.step}
              data-state={s.state}
              aria-pressed={s.key === selected}
              onClick={() => setSelected(s.key === selected ? null : s.key)}
            >
              <span className={styles.when}>
                {s.when}
                {STATE_WORD[s.state] && (
                  <span className={styles.stateWord}>
                    {s.when ? ' · ' : ''}
                    {STATE_WORD[s.state]}
                  </span>
                )}
              </span>
              <span className={styles.title}>{s.title}</span>
              {s.detail && <span className={styles.detail}>{s.detail}</span>}
            </button>
            {i < steps.length - 1 && (
              <span className={styles.arrow} aria-hidden="true">
                →
              </span>
            )}
          </li>
        ))}
      </ol>
      {current && (
        <div className={styles.open} data-testid="cause-step-open">
          <div className={styles.openHead}>
            {current.when && <span className={styles.when}>{current.when}</span>}
            <strong>{current.title}</strong>
          </div>
          {current.fact ? (
            <>
              <p className={styles.fact}>{current.fact.fact}</p>
              {current.fact.basis && (
                <p className={styles.basis}>
                  <span className={styles.basisLabel}>Merlin checked</span>
                  {current.fact.basis}
                </p>
              )}
            </>
          ) : (
            current.detail && <p className={styles.fact}>{current.detail}</p>
          )}
          {current.drives.length > 0 && (
            <div className={styles.drives}>
              <span className={styles.basisLabel}>What to do about it</span>
              <ul>
                {current.drives.map((d) => (
                  <li key={d.no}>
                    {numbered && <span className={styles.driveNo}>{d.no}.</span>}
                    <AssistantMarkdown content={d.text} />
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default CauseChain;
